import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';

import {showToast} from '@/components/Toaster/Toaster';
import mwSettingsService from '@/services/mw/mwSettingsService';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch} from '@/state/store';

export const useDeviceSettingBase = ({selector, valueConverter, uiValueConverter, content, autoSave = false}) => {
    const {notifications, type} = content || {};
    const reduxValue = useSelector(selector);
    const uiValueFromRedux = valueConverter ? valueConverter(reduxValue) : reduxValue;
    const [uiValue, setUiValue] = useState(uiValueFromRedux);

    const resetUIState = useCallback(() => {
        setUiValue(uiValueFromRedux);
    }, [uiValueFromRedux]);

    const handleSuccess = useCallback(
        (newValue) => {
            if (!notifications) return;
            const title = newValue ? notifications.onTitle : notifications.offTitle;

            showToast(title);
        },
        [notifications]
    );

    const handleError = useCallback(
        (newValue) => {
            if (!notifications) return;
            resetUIState();
            const title = newValue ? notifications.unsuccessfulOnTitle : notifications.unsuccessfulOffTitle;
            const description = newValue
                ? notifications.unsuccessfulOnDescription
                : notifications.unsuccessfulOffDescription;

            showToast(title, {
                description,
                action: {
                    label: notifications.unsuccessfulButton.text,
                    onClick: () => {
                        setUiValue(newValue);
                        updateSetting(newValue);
                    },
                },
            });
        },
        [notifications, resetUIState]
    );

    const updateSetting = useCallback(
        (value) => {
            if (!type) return;

            const apiValue = uiValueConverter ? uiValueConverter(value) : value;

            mwSettingsService.updateDeviceSettings({
                settingType: type,
                data: {[type]: apiValue},
                onUpdateStart: () => dispatch(showLoader()),
                onSuccess: () => {
                    dispatch(hideLoader());
                    handleSuccess(value);
                },
                onError: () => {
                    dispatch(hideLoader());
                    handleError(value);
                },
            });
        },
        [type, uiValueConverter, handleSuccess, handleError]
    );

    const onChange = useCallback(
        (newValue) => {
            setUiValue(newValue);
            if (autoSave) {
                updateSetting(newValue);
            }
        },
        [autoSave, updateSetting]
    );

    const onSave = useCallback(() => {
        updateSetting(uiValue);
    }, [uiValue, updateSetting]);

    return {
        uiValue,
        onChange,
        onSave,
        resetUIState,
        updateSetting,
    };
};
